import React from 'react';
import { Dialog, DialogTitle, DialogActions, Button, useTheme,
  useMediaQuery, DialogContent, Box, TextField, FormControlLabel, Switch } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardDocument } from './DashboardDocument';
import DashboardList from 'components/resources/dashboards/DashboardList';

export interface DashboardDialogProps {
  open: boolean;
  limitToTemplateDashboards?: boolean;
  onClose: () => void;
  onSubmit ?: (dashboard: DashboardDocument | undefined) => void;
  // Dashboards in the list must match the given dataset.
  datasetId?: string;
}

export default function DashboardDialog(props: DashboardDialogProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [searchString, setSearchString] = useState<string>("");
  const [selectedDashboardDocument, setSelectedDashboardDocument] = useState<DashboardDocument | undefined>();
  const [viewShared, setViewShared] = useState<boolean>(false);
  const [searchField, setSearchField] = useState<string>("name");
  const [currentPage, setCurrentPage] = useState<number>(1);

  return (
  <Dialog
    open={props.open}
    onClose={props.onClose}
    fullScreen={isSmallScreen}
    fullWidth={true}
    maxWidth="sm"
    sx={{
      margin:{xs:0, sm:0},
    }} >
    <DialogTitle>
      {i18n.t('resources.dashboards.list-title')}
      <Box sx={{float:'right', marginTop: "-15px"}}>
        <Box sx={{display:'inline-block', verticalAlign:'bottom', mr:2}}>
          <FormControlLabel
              control={
                <Switch
                  checked={viewShared}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setViewShared(event.target.checked);
                    setCurrentPage(1);
                  }}/>
              }
              label={i18n.t("resource-action.view-shared")}
              />
        </Box>
        <Box sx={{display:'inline-block', verticalAlign:'middle'}}>
          <TextField
            autoComplete="off"
            label={`${i18n.t('search')}...`}
            variant="standard"
            value={searchString || ""}
            onChange={((event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchString(event.target.value);
              setCurrentPage(1);
            })}/>
        </Box>
      </Box>
    </DialogTitle>
    <DialogContent sx={{
      p: isSmallScreen ? 0 : 3,
      pt:1, pb: 1,
      backgroundColor: theme.palette.background.default,
    }} dividers>
      <DashboardList
        datasetId={props.datasetId}
        viewShared={viewShared}
        searchString={searchString}
        searchField={searchField}
        limitToTemplateDashboards={props.limitToTemplateDashboards}
        selectedDashboardDocument={selectedDashboardDocument}
        setSelectedDashboardDocument={(dashboard: DashboardDocument | undefined) => {
          // If the same dashboard is selected, submit it
          if (dashboard && selectedDashboardDocument && dashboard.id === selectedDashboardDocument.id) {
            if (props.onSubmit) {
              props.onSubmit(dashboard);
            }
          }
          setSelectedDashboardDocument(dashboard);
        }}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </DialogContent>
    <DialogActions>
      <Button
        variant="outlined"
        onClick={props.onClose}>
        {i18n.t('cancel')}
      </Button>
      <Button
        disabled={!selectedDashboardDocument}
        variant="contained"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (props.onSubmit) {
            props.onSubmit(selectedDashboardDocument);
          }
        }}>
        {i18n.t('submit')}
      </Button>
    </DialogActions>
  </Dialog>
  );
}
